import React, { RefObject, useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { getProductId, getEnvironmentId, getProductName, getOrganizationId } from "utils/localStorage";
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store/redux/store';
import { setField as setRevenueAnalyticsField, updateSourceConfiguration as updateSourceConfigurationAction, serializeDatesInConfig } from 'store/redux/reducers/revenueAnalytics/revenueAnalyticsSlice';
import { CheckCircle, ChevronDown, Download, Upload, ArrowRight, AlertTriangle, Database, Globe, CreditCard, Building2, X, Eye, EyeOff, ExternalLink, File, Loader2, Copy, Calendar, ChevronDown as ChevronDownIcon, Activity } from 'lucide-react';

import { getFilteredStep2Configs, getRequiredSubsteps } from './utils/utils'
import { useListKeysV2LazyQuery, useCreateApiKeyV2Mutation } from "store/graphql/codegen/auto-gen/developer-api";
import { useGetEnvironmentsForProductLazyQuery, useCreateEnvironmentCaMutation, EnvironmentType } from "store/graphql/codegen/auto-gen/configurator";
import { developerAPIClient, configuratorClient } from "store/graphql/apollo/clients";
import { FieldMappingConfirmationModal } from './Elements/FieldMappingConfirmationModal';
import { getCurlCommands } from './utils/APICurls';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { ascetic, githubGist } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { TipsSection } from '../Common/TipsSection';
import { downloadStep2Sample } from '../../UseCases/RevenueAnalytics/utils/helpers'
import { StripeIntegration } from './Stripe/StripeIntegration';
import { ChargebeeIntegration } from './Chargebee/ChargebeeIntegration';
import { QuickBooksIntegration } from './QuickBooks/QuickBooksIntegration';
import { SiStripe } from 'react-icons/si';
import { EmailManager } from './RevenueAnalytics'

interface DataSetupProps {
  step2Configs: any[];
  step2UploadedFiles: (File | null)[];
  mapFieldsRefs: RefObject<(HTMLDivElement | null)[]>;
  nextButtonRefs: RefObject<(HTMLButtonElement | null)[]>;
  handleStep2FileUpload: (idx: number, e: React.ChangeEvent<HTMLInputElement>) => void;
  handleStep2FieldMappingChange: (idx: number, field: string, value: string) => void;
  processStep2Import: (idx: number) => void;
  onAutoSave: () => void;
  handleDownloadAllSamples: () => void;
  downloadFailedRecords: (subStepIdx: number) => void;
  fileInputRef: RefObject<HTMLInputElement>;
  getStepStatus: (idx: number) => string;
  renderImportResults: (importResult: any, subStepIdx: number) => React.ReactNode;
  getStatusDisplay: (status: string) => { color: string; label: string };
  getImportButtonText: (status: string, isProcessing: boolean) => string;
  retryStep2Import: (subStepIdx: number) => void;
  onStepComplete: (value: boolean, overrideValues?: any) => void;
  onApiKeyDataChange: (data: { selectedApiKeyId: string | null; selectedApiKey: string | null }) => void;
  initialApiKeyData?: { selectedApiKeyId: string | null; selectedApiKey: string | null };
  emailManager: EmailManager;
  cleanOAuthParams: () => void;
  openSetupGuideRef?: React.MutableRefObject<(() => void) | null>;
  onOpenIntegrationsList: () => void;
  onTrackCompletion?: () => void;
}

const sourceIcons = {
  csv: Database,
  api: Globe,
  stripe: SiStripe,
  chargebee: CreditCard,
  salesforce: Building2,
} as const;

const sourceLabels = {
  csv: 'Manual CSV Upload',
  api: 'API Integration',
  stripe: 'Stripe Integration',
  chargebee: 'Chargebee Integration',
  salesforce: 'Salesforce Integration',
};

const dataTypeLabels = {
  hierarchical: 'Hierarchical Data',
  invoice_transaction: 'Invoice + Transactions',
  plan_subscription: 'Plan + Subscription',
};

const dataTypeEndpoints = {
  hierarchical: ['customers', 'invoices', 'payments', 'plans', 'subscriptions'],
  invoice_transaction: ['invoices', 'transactions'],
  plan_subscription: ['plans', 'subscriptions'],
};

const authTypes = [
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'api_key', label: 'API Key' },
  { value: 'basic', label: 'Basic Auth' },
];

const statusConfig = {
  not_configured: { label: 'Not Configured', color: 'bg-gray-100 text-gray-600', icon: '' },
  in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-800', icon: '' },
  configured: { label: 'Configured', color: 'bg-green-50 text-green-800', icon: '' },
};

export function DataSetup({
  step2Configs,
  step2UploadedFiles,
  mapFieldsRefs,
  nextButtonRefs,
  handleStep2FileUpload,
  handleStep2FieldMappingChange,
  processStep2Import,
  fileInputRef,
  getStepStatus,
  renderImportResults,
  getStatusDisplay,
  getImportButtonText,
  retryStep2Import,
  onStepComplete,
  onAutoSave,
  onApiKeyDataChange,
  initialApiKeyData,
  emailManager,
  cleanOAuthParams,
  openSetupGuideRef,
  onOpenIntegrationsList,
  onTrackCompletion,
}: DataSetupProps) {
  const dispatch = useDispatch();
  const selectedSources = useSelector((s: RootState) => s.revenueAnalytics.selectedSources);
  const selectedDataType = useSelector((s: RootState) => s.revenueAnalytics.selectedDataType);
  const activeSourceTab = useSelector((s: RootState) => s.revenueAnalytics.activeSourceTab);
  const sourceConfigurations = useSelector((s: RootState) => s.revenueAnalytics.sourceConfigurations);
  const expandedStep2SubStep = useSelector((s: RootState) => s.revenueAnalytics.expandedStep2SubStep);
  const step2ImportResults = useSelector((s: RootState) => s.revenueAnalytics.step2ImportResults);
  const step2CsvHeaders = useSelector((s: RootState) => s.revenueAnalytics.step2CsvHeaders);
  const step2FieldMappings = useSelector((s: RootState) => s.revenueAnalytics.step2FieldMappings);
  const step2IsProcessing = useSelector((s: RootState) => s.revenueAnalytics.step2IsProcessing);
  const healthCheckCompletedStates = useSelector((s: RootState) => s.revenueAnalytics.healthCheckCompletedStates);
  const loadedStripeData = useSelector((s: RootState) => s.revenueAnalytics.loadedStripeData);

  const setActiveSourceTab = useCallback((sourceId: string) => {
    dispatch(setRevenueAnalyticsField({ key: 'activeSourceTab', value: sourceId }));
  }, [dispatch]);
  const updateSourceConfiguration = useCallback((sourceId: string, config: any) => {
    dispatch(updateSourceConfigurationAction({ sourceId, config: serializeDatesInConfig(config) }));
  }, [dispatch]);
  const setExpandedStep2SubStep = useCallback((idx: number) => {
    dispatch(setRevenueAnalyticsField({ key: 'expandedStep2SubStep', value: idx }));
  }, [dispatch]);
  const setHealthCheckCompletedStates = useCallback((states: Record<string, boolean>) => {
    dispatch(setRevenueAnalyticsField({ key: 'healthCheckCompletedStates', value: states }));
  }, [dispatch]);

  const healthCheckCompletedStatesRef = useRef(healthCheckCompletedStates);
  useEffect(() => {
    healthCheckCompletedStatesRef.current = healthCheckCompletedStates;
  }, [healthCheckCompletedStates]);

  const configStripe = sourceConfigurations?.stripe || {};
  const configChargebee = sourceConfigurations?.chargebee || {};
  const setConfigStripe = useCallback((c: any) => dispatch(updateSourceConfigurationAction({ sourceId: 'stripe', config: serializeDatesInConfig(c) })), [dispatch]);
  const setConfigChargebee = useCallback((c: any) => dispatch(updateSourceConfigurationAction({ sourceId: 'chargebee', config: serializeDatesInConfig(c) })), [dispatch]);

  const substepHeaderRefs = useRef<(HTMLDivElement | null)[]>([]);


  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingImportSubStepIdx, setPendingImportSubStepIdx] = useState<number | null>(null);
  const [isRetryAction, setIsRetryAction] = useState(false);


  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [apiAuthType, setApiAuthType] = useState<'bearer' | 'api_key' | 'basic'>('bearer');
  const [apiCredentials, setApiCredentials] = useState<Record<string, string>>({});
  const [apiEndpoints, setApiEndpoints] = useState<Record<string, string>>({});


  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [stripeWebhookEndpoint, setStripeWebhookEndpoint] = useState('');
  const [showStripeSecretKey, setShowStripeSecretKey] = useState(false);

  const [salesforceInstanceUrl, setSalesforceInstanceUrl] = useState('');
  const [salesforceUsername, setSalesforceUsername] = useState('');
  const [salesforcePassword, setSalesforcePassword] = useState('');
  const [salesforceSecurityToken, setSalesforceSecurityToken] = useState('');
  const [showSalesforcePassword, setShowSalesforcePassword] = useState(false);
  const [showSalesforceToken, setShowSalesforceToken] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [copiedEnvironment, setCopiedEnvironment] = useState<string | null>(null);
  const [selectedApiKey, setSelectedApiKey] = useState<string | null>(null);
  const [selectedApiKeyId, setSelectedApiKeyId] = useState<string | null>(null);
  const [showApiKeySelection, setShowApiKeySelection] = useState(false);
  const [showCreateApiKeyModal, setShowCreateApiKeyModal] = useState(false);
  const [showEnvironmentDropdown, setShowEnvironmentDropdown] = useState(false);
  const [showNewEnvironmentForm, setShowNewEnvironmentForm] = useState(false);
  const [createApiKeyForm, setCreateApiKeyForm] = useState({
    name: '',
    expirationDate: '',
    scope: 'revenue_apis',
    environment: 'Development',
    environmentId: '',
    environmentName: ''
  });
  const [newEnvironmentForm, setNewEnvironmentForm] = useState({
    name: '',
    displayName: '',
    environmentType: EnvironmentType.NonProduction
  });

  const [activeMethod, setActiveMethod] = useState<'POST' | 'PUT' | 'DELETE'>('POST');
  const [copiedCurl, setCopiedCurl] = useState(false);
  const [hasApiKeyChanged, setHasApiKeyChanged] = useState(false);
  const [lastSavedApiKeyId, setLastSavedApiKeyId] = useState<string | null>(null);

  const [websocketConnection, setWebsocketConnection] = useState<WebSocket | null>(null);
  const [healthCheckActive, setHealthCheckActive] = useState(false);
  const [healthCheckResults, setHealthCheckResults] = useState<any[]>([]);
  const [healthCheckCompleted, setHealthCheckCompleted] = useState(false);

  type ApiStepKey = 'transactions' | 'invoices' | 'subscriptions' | 'subscription-items';
  const [eventsByEntity, setEventsByEntity] = useState<Record<ApiStepKey, any[]>>({
    transactions: [],
    invoices: [],
    subscriptions: [],
    'subscription-items': [],
  });

  const [activeApiStep, setActiveApiStep] = useState<ApiStepKey>('transactions');
  const [expandedApiStep, setExpandedApiStep] = useState<'transactions' | 'invoices' | 'subscriptions' | 'subscription-items' | null>('transactions');

  const [selectedEventData, setSelectedEventData] = useState<string | null>(null);
  const [showEventDataModal, setShowEventDataModal] = useState(false);
  const [copiedEventData, setCopiedEventData] = useState(false);

  const currentConnectionKey = useRef('');

  const socketRef = useRef<WebSocket | null>(null);
  const socketGenerationRef = useRef(0);
  const activeTabRef = useRef(activeSourceTab);
  const onApiKeyDataChangeRef = useRef(onApiKeyDataChange);
  onApiKeyDataChangeRef.current = onApiKeyDataChange;

  const lastStepRef = useRef<HTMLDivElement>(null);
  const [lastStepHeight, setLastStepHeight] = useState<number | null>(null);
  
  const lastStepApiRef = useRef<HTMLDivElement>(null);
  const [lastStepApiHeight, setLastStepApiHeight] = useState<number | null>(null);

  const [ListKeysV2, { data: apiKeysData, loading: apiKeysLoading, error: apiKeysError }] = useListKeysV2LazyQuery({
    client: developerAPIClient,
    fetchPolicy: "no-cache"
  });

  const [createAPIKeyV2, { loading: createApiKeyLoading }] = useCreateApiKeyV2Mutation({
    client: developerAPIClient,
    fetchPolicy: "no-cache"
  });

  const [getEnvironmentsForProduct, { data: environmentsData, loading: environmentsLoading }] = useGetEnvironmentsForProductLazyQuery({
    client: configuratorClient,
    fetchPolicy: "no-cache"
  });

  const [createEnvironmentCaMutation, { loading: createEnvironmentLoading }] = useCreateEnvironmentCaMutation({
    client: configuratorClient,
    fetchPolicy: "no-cache"
  });

  const currentEnvironmentId = typeof window !== 'undefined' ? (getEnvironmentId() ?? '') : '';

  const apiKeysForCurrentEnvironment = React.useMemo(() => {
    if (!apiKeysData?.listKeysV2) return [];
    const active = apiKeysData.listKeysV2.filter((key): key is NonNullable<typeof key> => Boolean(key && key.isActive));
    if (!currentEnvironmentId) return active;
    return active.filter((key) => key.envId === currentEnvironmentId);
  }, [apiKeysData?.listKeysV2, currentEnvironmentId]);

  const environmentKeys = React.useMemo(() => {
    if (!apiKeysForCurrentEnvironment.length) {
      return {};
    }
    const keysByEnv: Record<string, string> = {};
    apiKeysForCurrentEnvironment.forEach((key) => {
      if (key && key.isActive) {
        keysByEnv[key.envName.toLowerCase()] = key.key;
      }
    });
    return keysByEnv;
  }, [apiKeysForCurrentEnvironment]);

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  useLayoutEffect(() => {
    activeTabRef.current = activeSourceTab;
  }, [activeSourceTab]);

  const getWebSocketUrl = () => {
    const productId = getProductId();
    const environmentId = selectedApiKeyId ?
      apiKeysData?.listKeysV2?.find(key => key?.id === selectedApiKeyId)?.envId :
      null;

    if (!productId || !environmentId) {
      console.error('Missing productId or environmentId for health check');
      return null;
    }

    const WS_API_URL = process.env.API_URL?.replace("https://", "wss://")

    const recordType = activeApiStep === 'invoices' ? 'INVOICE' : 
                       activeApiStep === 'subscriptions' ? 'SUBSCRIPTION' :
                       activeApiStep === 'subscription-items' ? 'SUBSCRIPTION_ITEM' : 'TRANSACTION';

    const operationType = activeMethod === 'PUT' ? 'UPDATE' :
      activeMethod === 'DELETE' ? 'DELETE' : 'CREATE';

    return `${WS_API_URL}/ws/revenueApiSetupEvents?productId=${productId}&environmentId=${environmentId}&recordType=${recordType}&operationType=${operationType}`;
  };

  const makeSocketConnection = useCallback(() => {
    const currentKey = `${activeApiStep}-${activeMethod}`;
    const socketKeyAtConnect = currentKey;
    const generationAtConnect = ++socketGenerationRef.current;

    if (currentConnectionKey.current !== currentKey) {
      console.log(`Connection key mismatch, skipping connection for ${currentKey}`);
      return;
    }

    if (!selectedSources.includes('api') || activeSourceTab !== 'api') {
      console.log('API not selected or not active tab, skipping WebSocket connection');
      currentConnectionKey.current = '';
      return;
    }

    if (socketRef.current) {
      closeSocketConnection();
    }

    const wsUrl = getWebSocketUrl();
    if (!wsUrl) {
      console.error('Could not construct WebSocket URL');
      currentConnectionKey.current = '';
      return;
    }

    console.log(`Creating WebSocket connection for ${currentKey}:`, wsUrl);
    socketRef.current = new WebSocket(wsUrl);

    socketRef.current.onopen = () => {
      console.log(`WebSocket opened for ${currentKey}`);
      setHealthCheckActive(true);
      setWebsocketConnection(socketRef.current);
    };

    socketRef.current.onmessage = (event) => {
      // Ignore late/outdated websocket messages from previous socket instances.
      if (socketGenerationRef.current !== generationAtConnect) return;
      onReceiveSocketMessage(event.data, socketKeyAtConnect);
    };

    socketRef.current.onerror = (error) => {
      console.error(`WebSocket error for ${currentKey}:`, error);
      setHealthCheckActive(false);
    };

    socketRef.current.onclose = (event) => {
      console.log(`WebSocket closed for ${currentKey}:`, event.code, event.reason);
      setHealthCheckActive(false);
      setWebsocketConnection(null);
      socketRef.current = null;

      if (currentConnectionKey.current === currentKey) {
        currentConnectionKey.current = '';
      }
    };
  }, [
    selectedSources,
    activeSourceTab,
    selectedApiKeyId,
    apiKeysData,
    activeApiStep,
    activeMethod
  ]);


  const onReceiveSocketMessage = async (message: any, socketKey: string) => {
    try {
      const msg = JSON.parse(message);
      console.log('WebSocket message:', msg);

      if (!msg || !msg.revenue_events || !Array.isArray(msg.revenue_events)) {
        console.log('No revenue_events found in message');
        return;
      }

      if (msg.revenue_events.length > 0) {
        const getEntityKeyFromRecordType = (recordType: string): ApiStepKey | null => {
          switch (recordType) {
            case 'TRANSACTION':
              return 'transactions';
            case 'INVOICE':
              return 'invoices';
            case 'SUBSCRIPTION':
              return 'subscriptions';
            case 'SUBSCRIPTION_ITEM':
              return 'subscription-items';
            default:
              return null;
          }
        };

        const newEventsByEntity: Partial<Record<ApiStepKey, any[]>> = {};

        msg.revenue_events.forEach((event: any) => {
          const entityKey = getEntityKeyFromRecordType(event?.record_type);
          if (!entityKey) return;

          const normalizedEvent = {
            ...event,
            type: event.record_type || 'UNKNOWN',
            name: event.operation_type || '',
            timestamp: event.timestamp || new Date().toISOString(),
            id: event.id || Math.random().toString(36).substr(2, 9),
            originalData: (() => {
              try {
                const dataToShow = event.data || event.payload || event;

                if (typeof dataToShow === 'string' && dataToShow.startsWith('{')) {
                  const parsed = JSON.parse(dataToShow);
                  return JSON.stringify(parsed, null, 2);
                }

                return JSON.stringify(dataToShow, null, 2);
              } catch {
                return JSON.stringify(event, null, 2);
              }
            })(),
          };

          if (!newEventsByEntity[entityKey]) newEventsByEntity[entityKey] = [];
          newEventsByEntity[entityKey]!.push(normalizedEvent);
        });

        setEventsByEntity(prev => {
          const next = { ...prev };
          (Object.keys(newEventsByEntity) as ApiStepKey[]).forEach((key) => {
            const incoming = newEventsByEntity[key] ?? [];
            next[key] = [...incoming, ...prev[key]].slice(0, 5);
          });
          return next;
        });

        setHealthCheckResults(prev => [...prev, ...msg.revenue_events]);

        const hasSuccessfulEvents = msg.revenue_events.some(
          (event: any) => event.processing_status === 'COMPLETED'
        );

        if (hasSuccessfulEvents) {
          const prev = healthCheckCompletedStatesRef.current;
          const newStates = {
            ...prev,
            [socketKey]: true,
          };

          // Update ref immediately so rapid subsequent websocket messages see the latest state.
          healthCheckCompletedStatesRef.current = newStates;

          if (
            newStates['transactions-POST'] &&
            newStates['invoices-POST'] &&
            newStates['subscriptions-POST'] &&
            newStates['subscription-items-POST'] &&
            selectedApiKey &&
            selectedApiKeyId
          ) {
            updateSourceConfiguration('api', {
              ...sourceConfigurations.api,
              status: 'configured',
              completed: true,
              completedAt: new Date().toISOString(),
            });
          }

          setHealthCheckCompletedStates(newStates);
          setHealthCheckCompleted(true);
          closeSocketConnection();
        }
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  };

  useEffect(() => {
    setEventsByEntity(prev => ({ ...prev, [activeApiStep]: [] }));
  }, [activeApiStep]);

  useLayoutEffect(() => {
    if (lastStepRef.current) {
      const height = lastStepRef.current.getBoundingClientRect().height;
      setLastStepHeight(height);
    }
  }, [expandedStep2SubStep]);
  
  useLayoutEffect(() => {
    if (lastStepApiRef.current) {
      const height = lastStepApiRef.current.getBoundingClientRect().height;
      setLastStepApiHeight(height);
    }
  }, [activeApiStep]);


  const closeSocketConnection = useCallback(() => {
    console.log('closeSocketConnection called, socketRef.current:', socketRef.current);

    if (!socketRef.current) {
      console.log('No socket to close');
      return;
    }

    console.log('Closing WebSocket connection, readyState:', socketRef.current.readyState);

    if (socketRef.current.readyState === WebSocket.OPEN || socketRef.current.readyState === WebSocket.CONNECTING) {
      socketRef.current.close();
    }

    socketRef.current = null;
    // Invalidate any in-flight onmessage handlers from older websocket instances.
    socketGenerationRef.current += 1;
    setHealthCheckActive(false);
    setWebsocketConnection(null);
    currentConnectionKey.current = '';
    console.log('WebSocket connection closed and state updated');
  }, []);


  useEffect(() => {
    const currentKey = `${activeApiStep}-${activeMethod}`;
    if (currentConnectionKey.current === currentKey && healthCheckActive) {
      return;
    }

    const shouldStartHealthCheck =
      selectedSources.includes('api') &&
      activeSourceTab === 'api' &&
      selectedApiKeyId &&
      apiKeysData?.listKeysV2;

    if (shouldStartHealthCheck) {
      console.log(`Starting WebSocket for ${currentKey}`);

      if (socketRef.current) {
        closeSocketConnection();
      }

      currentConnectionKey.current = currentKey;

      const timer = setTimeout(() => {
        makeSocketConnection();
      }, 1000);

      return () => {
        clearTimeout(timer);
      };
    } else if (activeSourceTab !== 'api' && healthCheckActive) {
      console.log('Switched away from API tab - stopping WebSocket');
      closeSocketConnection();
      currentConnectionKey.current = '';
    }
  }, [
    selectedSources,
    activeSourceTab,
    selectedApiKeyId,
    apiKeysData,
    activeApiStep,
    activeMethod,
    makeSocketConnection,
    closeSocketConnection
  ]);


  useEffect(() => {
    const handleBeforeUnload = () => {
      closeSocketConnection();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        ;
        closeSocketConnection();
      }
    };


    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);


    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      closeSocketConnection();
    };
  }, [closeSocketConnection]);


  const initialApiKeyId = initialApiKeyData?.selectedApiKeyId ?? null;
  const initialApiKeyValue = initialApiKeyData?.selectedApiKey ?? null;

  useEffect(() => {
    const keysForEnv = apiKeysForCurrentEnvironment;
    if (!keysForEnv.length) return;

    const notifyApiKeyChange = (data: { selectedApiKeyId: string | null; selectedApiKey: string | null }) => {
      if (
        data.selectedApiKeyId !== initialApiKeyId ||
        data.selectedApiKey !== initialApiKeyValue
      ) {
        onApiKeyDataChangeRef.current(data);
      }
    };

    if (selectedApiKey) {
      const selectedInCurrentEnv = keysForEnv.some((key) => key && key.key === selectedApiKey);
      if (!selectedInCurrentEnv) {
        setSelectedApiKey(null);
        setSelectedApiKeyId(null);
        notifyApiKeyChange({ selectedApiKeyId: null, selectedApiKey: null });
      }
      return;
    }

    if (initialApiKeyId) {
      const matchingKey = keysForEnv.find((key) => key && key.id === initialApiKeyId);
      if (matchingKey) {
        setSelectedApiKey(matchingKey.key);
        setSelectedApiKeyId(matchingKey.id);
        notifyApiKeyChange({
          selectedApiKeyId: matchingKey.id,
          selectedApiKey: matchingKey.key
        });
        return;
      }
    }

    if (keysForEnv.length === 1 && keysForEnv[0]) {
      setSelectedApiKey(keysForEnv[0].key);
      setSelectedApiKeyId(keysForEnv[0].id);
      notifyApiKeyChange({
        selectedApiKeyId: keysForEnv[0].id,
        selectedApiKey: keysForEnv[0].key
      });
    }
  }, [apiKeysForCurrentEnvironment, selectedApiKey, initialApiKeyId, initialApiKeyValue]);

  useEffect(() => {

    fetchApiKeys();
  }, [])

  useEffect(() => {
    return () => {
      closeSocketConnection();
    };
  }, [closeSocketConnection]);


  const apiConfigSignature = typeof sourceConfigurations?.api === 'object'
    ? JSON.stringify({
        baseUrl: sourceConfigurations.api.baseUrl,
        authType: sourceConfigurations.api.authType,
        credentials: sourceConfigurations.api.credentials,
        endpoints: sourceConfigurations.api.endpoints
      })
    : '';

  useEffect(() => {
    if (sourceConfigurations?.api) {
      const apiConfig = sourceConfigurations.api;
      setApiBaseUrl(apiConfig.baseUrl || '');
      setApiAuthType(apiConfig.authType || 'bearer');
      setApiCredentials(apiConfig.credentials || {});
      setApiEndpoints(apiConfig.endpoints || {});
    }
  }, [apiConfigSignature]);

  const salesforceConfigSignature = typeof sourceConfigurations?.salesforce === 'object'
    ? JSON.stringify({
        instanceUrl: sourceConfigurations.salesforce.instanceUrl,
        username: sourceConfigurations.salesforce.username,
        password: sourceConfigurations.salesforce.password,
        securityToken: sourceConfigurations.salesforce.securityToken
      })
    : '';

  useEffect(() => {
    if (sourceConfigurations?.salesforce) {
      const salesforceConfig = sourceConfigurations.salesforce;
      setSalesforceInstanceUrl(salesforceConfig.instanceUrl || '');
      setSalesforceUsername(salesforceConfig.username || '');
      setSalesforcePassword(salesforceConfig.password || '');
      setSalesforceSecurityToken(salesforceConfig.securityToken || '');
    }
  }, [salesforceConfigSignature]);

  useEffect(() => {
    substepHeaderRefs.current = substepHeaderRefs.current.slice(0, step2Configs.length);
  }, [step2Configs.length]);

  const selectedSourcesKey = (selectedSources?.length ?? 0) > 0
    ? (selectedSources || []).slice().sort().join(',')
    : '';

  useEffect(() => {
    if (selectedSources.length > 0 && !activeSourceTab) {
      setActiveSourceTab(selectedSources[0]);
    }

    if (activeSourceTab && !selectedSources.includes(activeSourceTab)) {
      setActiveSourceTab(selectedSources[0] || '');
    }
  }, [selectedSourcesKey, activeSourceTab, setActiveSourceTab]);

  useEffect(() => {

    const hasAnyImportResult = step2ImportResults.some(result =>
      result !== null && result !== undefined
    );

    if (hasAnyImportResult && onAutoSave) {
      const importResults = step2ImportResults.filter(result =>
        result !== null && result !== undefined
      );

      const successful = importResults.filter(r => r.success).length;
      const failed = importResults.filter(r => !r.success).length;
      const withErrors = importResults.filter(r => r.success && r.errorRows > 0).length;

      onAutoSave();
    }
    //onStepComplete(false);
  }, [step2ImportResults]);

  useEffect(() => {
    if (initialApiKeyId && apiKeysData?.listKeysV2 && !lastSavedApiKeyId) {
      setLastSavedApiKeyId(initialApiKeyId);
      setHasApiKeyChanged(false);
    }
  }, [initialApiKeyId, apiKeysData?.listKeysV2, lastSavedApiKeyId]);

  useEffect(() => {
    let progressInterval: NodeJS.Timeout | undefined;
    let slowProgressInterval: NodeJS.Timeout | undefined;
    let completionTimeout: NodeJS.Timeout | undefined;

    const isCurrentStepProcessing = expandedStep2SubStep >= 0 && step2IsProcessing[expandedStep2SubStep];

    if (isCurrentStepProcessing) {

      setProgressPercentage(0);

      const initialTarget = 80;
      const initialDuration = 3500;
      const intervalTime = 50;
      const initialIncrement = (initialTarget / initialDuration) * intervalTime;

      progressInterval = setInterval(() => {
        setProgressPercentage(prev => {
          const newProgress = prev + initialIncrement;
          if (newProgress >= initialTarget) {
            clearInterval(progressInterval);


            slowProgressInterval = setInterval(() => {
              setProgressPercentage(current => {

                const slowIncrement = 0.1;
                const newValue = current + slowIncrement;
                return Math.min(newValue, 99);
              });
            }, intervalTime);

            return initialTarget;
          }
          return newProgress;
        });
      }, intervalTime);
    } else if (progressPercentage > 0 && progressPercentage < 100 && expandedStep2SubStep >= 0 && !step2IsProcessing[expandedStep2SubStep]) {

      if (slowProgressInterval) clearInterval(slowProgressInterval);

      completionTimeout = setTimeout(() => {
        setProgressPercentage(100);


        setTimeout(() => {
          setProgressPercentage(0);
        }, 2000);
      }, 300);
    }

    return () => {
      if (progressInterval) clearInterval(progressInterval);
      if (slowProgressInterval) clearInterval(slowProgressInterval);
      if (completionTimeout) clearTimeout(completionTimeout);
    };
  }, [expandedStep2SubStep, step2IsProcessing]);

  const handleImportButtonClick = (subStepIdx: number, isRetry: boolean = false) => {
    setPendingImportSubStepIdx(subStepIdx);
    setIsRetryAction(isRetry);
    setShowConfirmationModal(true);
  };

  const fetchApiKeys = async () => {
    try {
      await ListKeysV2({
        variables: {
          input: {
            productId: getProductId() as string,
            scope: "revenue_apis",
            status: "active"
          }
        }
      });
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    }
  };

  const handleCopyApiKey = async (environment: string) => {
    try {
      const apiKey = environmentKeys[environment];
      if (apiKey) {
        await navigator.clipboard.writeText(apiKey);
        setCopiedEnvironment(environment);
        setTimeout(() => setCopiedEnvironment(null), 2000);
      }
    } catch (err) {
      console.error('Failed to copy API key:', err);
    }
  };


  const handleCreateNewApiKey = async () => {

    try {
      await getEnvironmentsForProduct({
        variables: {
          input: {
            productId: getProductId() as string
          }
        }
      });
    } catch (error) {
      console.error('Failed to fetch environments:', error);
    }
    setShowCreateApiKeyModal(true);
  };

  const handleCreateApiKeySubmit = async () => {
    if (!createApiKeyForm.name || !createApiKeyForm.expirationDate || !createApiKeyForm.environmentId) {
      return;
    }

    try {
      const validTill = new Date(createApiKeyForm.expirationDate).getTime() / 1000;

      const result = await createAPIKeyV2({
        variables: {
          input: {
            productId: getProductId() as string,
            name: createApiKeyForm.name,
            scopes: ["revenue_apis"],
            validTill: Math.floor(validTill),
            envId: createApiKeyForm.environmentId,
            envName: createApiKeyForm.environmentName,
            sbId: getOrganizationId() as string
          }
        }
      });

      if (result.data?.createAPIKeyV2) {

        await fetchApiKeys();


        const newApiKey = result.data.createAPIKeyV2;
        if (newApiKey) {
          setSelectedApiKey(newApiKey.key);
          setSelectedApiKeyId(newApiKey.id);


          setHasApiKeyChanged(true);


          onApiKeyDataChange({
            selectedApiKeyId: newApiKey.id,
            selectedApiKey: newApiKey.key
          });
        }

        setShowCreateApiKeyModal(false);

        setCreateApiKeyForm({
          name: '',
          expirationDate: '',
          scope: 'revenue_apis',
          environment: 'Development',
          environmentId: '',
          environmentName: ''
        });
      } else {
        console.error('Failed to create API key');
      }
    } catch (error) {
      console.error('Error creating API key:', error);
    }
  };

  const handleCreateApiKeyCancel = () => {
    setShowCreateApiKeyModal(false);
    setShowEnvironmentDropdown(false);
    setShowNewEnvironmentForm(false);

    setCreateApiKeyForm({
      name: '',
      expirationDate: '',
      scope: 'revenue_apis',
      environment: 'Development',
      environmentId: '',
      environmentName: ''
    });
    setNewEnvironmentForm({
      name: '',
      displayName: '',
      environmentType: EnvironmentType.NonProduction
    });
  };

  const handleEnvironmentSelect = (env: any) => {
    setCreateApiKeyForm(prev => ({
      ...prev,
      environmentId: env.id,
      environmentName: env.displayName,
      environment: env.displayName
    }));
    setShowEnvironmentDropdown(false);
  };

  const handleNewEnvironment = () => {
    setShowEnvironmentDropdown(false);
    setShowNewEnvironmentForm(true);
  };

  const handleCreateNewEnvironment = async () => {
    if (!newEnvironmentForm.displayName) {
      return;
    }

    try {
      const result = await createEnvironmentCaMutation({
        variables: {
          input: {
            name: newEnvironmentForm.displayName.toLowerCase().replace(/\s+/g, '-'),
            displayName: newEnvironmentForm.displayName,
            environmentType: newEnvironmentForm.environmentType as EnvironmentType
          }
        }
      });

      if (result.data?.createEnvironmentCA?.isSuccess) {

        setNewEnvironmentForm({
          name: '',
          displayName: '',
          environmentType: EnvironmentType.NonProduction
        });
        setShowNewEnvironmentForm(false);


        await getEnvironmentsForProduct({
          variables: {
            input: {
              productId: getProductId() as string
            }
          }
        });
      } else {
        console.error('Failed to create environment');
      }
    } catch (error) {
      console.error('Error creating environment:', error);
    }
  };

  const handleCancelNewEnvironment = () => {
    setShowNewEnvironmentForm(false);
    setNewEnvironmentForm({
      name: '',
      displayName: '',
      environmentType: EnvironmentType.NonProduction
    });
  };


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (showEnvironmentDropdown && !target.closest('[data-dropdown="environment"]')) {
        setShowEnvironmentDropdown(false);
      }

      if (showApiKeySelection && !target.closest('[data-dropdown="api-key"]')) {
        setShowApiKeySelection(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEnvironmentDropdown, showApiKeySelection]);


  const handleConfirmImport = () => {
    if (pendingImportSubStepIdx !== null) {
      if (isRetryAction) {
        retryStep2Import(pendingImportSubStepIdx);
      } else {
        processStep2Import(pendingImportSubStepIdx);
      }
    }
    setShowConfirmationModal(false);
    setPendingImportSubStepIdx(null);
    setIsRetryAction(false);
  };


  const handleEditMapping = () => {
    setShowConfirmationModal(false);
    setPendingImportSubStepIdx(null);
    setIsRetryAction(false);

  };


  const handleSaveSalesforceConfig = () => {
    if (!selectedSources.includes('salesforce')) return;

    const isConfigured = salesforceInstanceUrl && salesforceUsername && salesforcePassword && salesforceSecurityToken;

    updateSourceConfiguration('salesforce', {
      status: isConfigured ? 'configured' : 'in_progress',
      instanceUrl: salesforceInstanceUrl,
      username: salesforceUsername,
      password: salesforcePassword,
      securityToken: salesforceSecurityToken,
    });
  };


  if (selectedSources.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">
          No data sources selected. Please go back to select your data sources.
        </div>
      </div>
    );
  }


  const filteredStep2Configs = getFilteredStep2Configs(selectedDataType, step2Configs);
  const requiredSubstepKeys = getRequiredSubsteps(selectedDataType, step2Configs);

  const renderCSVConfiguration = () => {
    const dataType = selectedDataType || 'hierarchical';

    return (
      <div className="space-y-6 overflow-y-auto">

        {/* CSV Upload Section - Using filtered configs */}
        <div className="relative">
          {/* Connector line for CSV steps */}
          {filteredStep2Configs.length > 1 && (
            <div
              className="absolute left-5 w-0.5 bg-gray-300"
              style={{
                top: '4.3rem',
                bottom: lastStepHeight ? `${lastStepHeight - 10}px` : '4rem',
              }}
            />
          )}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 relative z-10 ml-10">
            {filteredStep2Configs.map((subStep, idx) => {
              const originalIdx = step2Configs.findIndex(config => config.key === subStep.key);
              const isActive = expandedStep2SubStep === originalIdx;
              const status = getStepStatus(originalIdx);
              const statusConfig = getStatusDisplay(status);

              const isRequired = requiredSubstepKeys.includes(subStep.key);

              return (
                <div
                  key={subStep.key}
                  ref={idx === filteredStep2Configs.length - 1 ? lastStepRef : null}
                  className={`border-b border-gray-200 last:border-b-0 ${isActive ? 'bg-blue-50' : ''}`}
                >
                  <button
                    type="button"
                    ref={el => { substepHeaderRefs.current[originalIdx] = el as HTMLDivElement | null; }}
                    onClick={() => {
                      const wasActive = isActive;
                      setExpandedStep2SubStep(isActive ? -1 : originalIdx);
                    }}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {/* Step numbers */}
                      <div className="absolute -left-10 w-8 h-8 bg-blue-500 font-semibold border-2 text-white flex-shrink-0 flex items-center justify-center rounded-full text-sm z-10">
                        {idx + 1}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="font-semibold text-gray-900">{subStep.title}</div>
                          {isRequired ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-800 h-5">
                              Required
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Optional
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 me-2">{subStep.description}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isActive ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                  {isActive && (
                    <div className="border-t border-gray-200 p-6 bg-white">
                      {renderStep2SubStep(originalIdx)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderAPIConfiguration= () => {
    const dataType = selectedDataType || 'hierarchical';
    const requiredEndpoints = dataTypeEndpoints[dataType as keyof typeof dataTypeEndpoints] || [];
    const isApiCompleted = sourceConfigurations.api?.completed === true;

    const apiKeyForCurl = selectedApiKey || '<Your API Key with Revenue APIs Scope>';
    const curlCommands = getCurlCommands(apiKeyForCurl);
    const currentCurl = curlCommands[activeApiStep][activeMethod];

    const handleCopyCurl = async () => {
      try {
        await navigator.clipboard.writeText(currentCurl);
        setCopiedCurl(true);
        setTimeout(() => setCopiedCurl(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    };

    if (apiKeysLoading) {
      return (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center space-y-4">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                <p className="text-gray-600">Loading API configuration...</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="space-y-6">
          <div className="border border-gray-200 rounded-lg p-6">
            <div className='bg-white rounded-lg'>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">API Keys</h3>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowNewEnvironmentForm(true)}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                  >
                    + Add Environment
                  </button>
                  <button
                    onClick={handleCreateNewApiKey}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                  >
                    + Create API Key
                  </button>
                </div>
              </div>
              <div className='flex flex-col ms-6'>
                {/* No API Keys Found State */}
                {!apiKeysLoading && !apiKeysError && apiKeysForCurrentEnvironment.length === 0 && (
                  <div className="text-center py-6">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Database className="w-6 h-6 text-gray-400" />
                      </div>
                      <h4 className="text-md font-medium text-gray-900 mb-2">No API Keys Found</h4>
                      <p className="text-gray-600 text-sm">
                        No API keys found for the current environment with scope <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs">Revenue APIs</span>
                      </p>
                      <button
                        onClick={handleCreateNewApiKey}
                        className="text-blue-600 hover:text-blue-700 font-medium text-sm mt-6"
                      >
                        + Create API Key
                      </button>
                    </div>
                  </div>
                )}

                {/* API Keys Dropdown */}
                {!apiKeysLoading && !apiKeysError && apiKeysForCurrentEnvironment.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select API Key
                    </label>
                    <div className="relative mb-6" data-dropdown="api-key">
                      <button
                        type="button"
                        onClick={() => setShowApiKeySelection(!showApiKeySelection)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between"
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {selectedApiKey ?
                                apiKeysForCurrentEnvironment.find(key => key?.key === selectedApiKey)?.name || 'Select API Key'
                                : 'Select API Key'
                              }
                            </div>
                            <div className="text-xs text-gray-500 font-mono truncate">
                              {selectedApiKey ?
                                `${selectedApiKey.substring(0, 8)}...${selectedApiKey.substring(selectedApiKey.length - 8)}`
                                : 'No API key selected'
                              }
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (selectedApiKey) {
                                const envName = apiKeysForCurrentEnvironment.find(key => key && key.key === selectedApiKey)?.envName?.toLowerCase() ?? '';
                                handleCopyApiKey(envName);
                              }
                            }}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            disabled={!selectedApiKey}
                          >
                            {copiedEnvironment === apiKeysForCurrentEnvironment.find(key => key && key.key === selectedApiKey)?.envName?.toLowerCase() ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <div className="flex items-center space-x-2 ml-2">
                          {selectedApiKey && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {apiKeysForCurrentEnvironment.find(key => key && key.key === selectedApiKey)?.envName}
                            </span>
                          )}
                          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showApiKeySelection ? 'rotate-180' : ''}`} />
                        </div>
                      </button>

                      {/* API Keys Dropdown Menu */}
                      {showApiKeySelection && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                          <div className="py-1">
                            {apiKeysForCurrentEnvironment.map((key) => {
                              if (!key) return null;
                              const isSelected = selectedApiKey === key.key;

                              return (
                                <button
                                  key={key.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedApiKey(key.key);
                                    setSelectedApiKeyId(key.id);
                                    setShowApiKeySelection(false);

                                    const hasChanged = key.id !== lastSavedApiKeyId;
                                    setHasApiKeyChanged(hasChanged);

                                    onApiKeyDataChange({
                                      selectedApiKeyId: key.id,
                                      selectedApiKey: key.key
                                    });
                                  }}
                                  className={`w-full px-3 py-2 text-left hover:bg-blue-50 hover:text-blue-900 flex items-center justify-between ${isSelected ? 'bg-blue-50 text-blue-900' : ''}`}
                                >
                                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium truncate">{key.name}</div>
                                      <div className="text-xs text-gray-500 font-mono truncate">
                                        {key.key ? `${key.key.substring(0, 8)}...${key.key.substring(key.key.length - 8)}` : 'No key available'}
                                      </div>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyApiKey(key.envName.toLowerCase());
                                      }}
                                      className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                                    >
                                      {copiedEnvironment === key.envName.toLowerCase() ? (
                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                      ) : (
                                        <Copy className="w-4 h-4" />
                                      )}
                                    </button>
                                  </div>
                                  <div className="flex items-center space-x-2 ml-2">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                      {key.envName}
                                    </span>
                                    {isSelected && (
                                      <CheckCircle className="w-4 h-4 text-blue-600" />
                                    )}
                                  </div>
                                </button>
                              );
                            })}

                            <div className="border-t border-gray-200">
                              <button
                                type="button"
                                onClick={() => {
                                  setShowApiKeySelection(false);
                                  handleCreateNewApiKey();
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 hover:text-blue-700 flex items-center space-x-2"
                              >
                                <span className="text-lg">+</span>
                                <span>Create API Key</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* API Key Change Warning Banner */}
                {hasApiKeyChanged && (
                  <div className="my-4 p-3 bg-yellow-50 border rounded-lg">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-600" />
                      <span className="text-yellow-800 text-sm font-medium">
                        API key has been changed. Click "Confirm Changes" after completing steps to save the new selection.
                      </span>
                    </div>
                  </div>
                )}

                {apiKeysError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700 text-sm">
                      Failed to load API keys. Please try again or contact support.
                    </p>
                    <button
                      onClick={fetchApiKeys}
                      className="mt-2 text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Retry
                    </button>
                  </div>
                )}

                <TipsSection
                  title="Know more about API Key"
                  content={
                    <ul className="space-y-2">
                      <li key={1} className="text-sm text-amber-800 flex items-start">
                        <span className="mr-2">•</span>
                        <span>
                          Your selected API key for the Revenue Analytics is included in the script below.
                        </span>
                      </li>
                      <li key={2} className="text-sm text-amber-800 flex items-start">
                        <span className="mr-2">•</span>
                        <span>
                          Store it in a configuration file to easily switch keys between
                          different environments.
                        </span>
                      </li>
                      <li key={3} className="text-sm text-amber-800 flex items-start">
                        <span className="mr-2">•</span>
                        <span>
                          You can generate separate API keys for production and other
                          environments above.
                        </span>
                      </li>
                    </ul>
                  }
                />
              </div>
            </div>


            {/* API Steps Section */}
            <div className="mt-8">

              <div className="relative">
                {/* Connector line for API steps */}
                <div
                  className="absolute left-5 w-0.5 bg-gray-300"
                  style={{
                    top: '4rem',
                    bottom: lastStepApiHeight ? `${lastStepApiHeight}px` : '2rem',
                  }}
                />
                <div className="space-y-4 relative z-10 ml-10">
                  {/* Transactions Step */}
                  <div className={`border border-gray-200 rounded-lg ${expandedApiStep === 'transactions' ? 'bg-blue-50' : 'bg-white'}`}>
                    <button
                      type="button"
                      onClick={() => {
                        const newExpanded = expandedApiStep === 'transactions' ? null : 'transactions';
                        setExpandedApiStep(newExpanded);
                        if (newExpanded === 'transactions') {
                          setActiveApiStep('transactions');
                        }
                      }}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="absolute -left-10 w-8 h-8 bg-blue-500 font-semibold border-2 text-white flex-shrink-0 flex items-center justify-center rounded-full text-sm z-10">
                          1
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">Transaction</div>
                          <div className="text-sm text-gray-500">API endpoint for sending Transaction data</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${healthCheckCompletedStates['transactions-POST']
                          ? 'bg-green-50 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                          }`}>
                          {healthCheckCompletedStates['transactions-POST'] ? 'Complete' : 'Pending'}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedApiStep === 'transactions' ? 'rotate-180' : ''
                          }`} />
                      </div>
                    </button>

                    {expandedApiStep === 'transactions' && (
                      <div className="border-t border-gray-200 p-6 bg-white">
                        <div className="bg-gray-50 rounded-lg p-4 relative overflow-x-auto">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex space-x-1">
                              {([
                                { method: 'POST', label: 'CREATE' },
                                { method: 'PUT', label: 'UPDATE' },
                                { method: 'DELETE', label: 'DELETE' }
                              ] as const).map(({ method, label }) => (
                                <button
                                  key={method}
                                  onClick={() => setActiveMethod(method)}
                                  className={`px-3 py-1 text-sm font-medium rounded transition-colors ${activeMethod === method
                                    ? 'bg-primaryColor text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <button
                              onClick={handleCopyCurl}
                              className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 transition-colors"
                              title="Copy to clipboard"
                            >
                              {copiedCurl ? (
                                <>
                                  <CheckCircle className="w-3 h-3 text-green-600" />
                                  <span className="text-green-600">Copied!</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-3 h-3" />
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                          </div>
                          <SyntaxHighlighter
                            language="javascript"
                            style={githubGist}
                            customStyle={{
                              fontSize: '0.875rem',
                              background: '#f9fafb',
                              borderRadius: '.5rem',
                              padding: '1rem',
                              maxWidth: '100%',
                              overflowX: 'auto'
                            }}
                            codeTagProps={{ style: { fontFamily: 'Menlo, monospace' } }}
                          >
                            {curlCommands.transactions[activeMethod]}
                          </SyntaxHighlighter>
                        </div>
                        {/* Info Message */}
                        <div className="bg-amber-50 border rounded-lg p-3 mt-4">
                          <p className="text-amber-900 text-sm">
                            Use the above create cURL commands to send events to complete the Step.
                          </p>
                        </div>

                        {/* Status for Transactions */}
                        <div className="mt-4 p-4 border rounded-lg bg-blue-50">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="flex items-center justify-center w-6 h-6">
                              {healthCheckCompletedStates['transactions-POST'] ? (
                                <CheckCircle className="w-6 h-6 text-green-600 font-bold" />
                              ) : (
                                <Loader2 className="w-6 h-6 text-blue-500 animate-spin font-bold" strokeWidth={4} />
                              )}
                            </span>
                            <span className={`font-medium ${healthCheckCompletedStates['transactions-POST'] ? 'text-green-700' : 'text-blue-700'
                              }`}>
                              {healthCheckCompletedStates['transactions-POST']
                                ? 'Transaction events detected successfully'
                                : 'Waiting for events...'}
                            </span>
                          </div>

                          {/* Recent Events Table for Transactions */}
                          {expandedApiStep === 'transactions' && eventsByEntity.transactions.length > 0 && (
                            <div className="mt-4 transition-all duration-500 ease-out opacity-100 transform translate-y-0">
                              <div className="bg-white rounded-lg border border-blue-200 p-4 ms-8">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Activity className="w-5 h-5 text-blue-600" />
                                  <h4 className="font-medium text-blue-800">Recent Transaction Events</h4>
                                </div>
                                <div className="overflow-x-auto">
                                  <table className="w-full">
                                    <thead>
                                      <tr className="bg-gray-50 text-left">
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Event Type</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Operation</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Status</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Timestamp</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Data</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {eventsByEntity.transactions.map((event, index) => (
                                        <tr
                                          key={`${event.id}-${index}`}
                                          style={{
                                            opacity: 0,
                                            transform: 'translateY(20px)',
                                            animation: `fade-in-up 0.5s ease-out ${index * 0.1}s forwards`,
                                          }}
                                        >
                                          <td className="px-4 py-3">
                                            <div className="flex items-center space-x-2">
                                              <Activity className="w-4 h-4 text-blue-600" />
                                              <span className="font-medium text-gray-900">{event.record_type}</span>
                                            </div>
                                          </td>
                                          <td className="px-4 py-3">
                                            <span className="font-medium text-gray-900">
                                              {event.operation_type || '-'}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${event.processing_status === 'COMPLETED'
                                              ? 'bg-green-50 text-green-800'
                                              : event.processing_status === 'FAILED'
                                                ? 'bg-red-500 text-red-800'
                                                : 'bg-yellow-100 text-yellow-800'
                                              }`}>
                                              {event.processing_status || 'PENDING'}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3 text-gray-600">
                                            {formatTime(event.timestamp)}
                                          </td>
                                          <td className="px-4 py-3">
                                            <button
                                              onClick={() => {
                                                setSelectedEventData(event.originalData);
                                                setShowEventDataModal(true);
                                              }}
                                              className="text-blue-600 hover:text-blue-800 font-mono text-sm px-2 py-1 hover:bg-blue-50 transition-colors"
                                              title="View original JSON data"
                                            >
                                              ...
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Invoices Step */}
                  <div 
                    className={`border border-gray-200 rounded-lg ${expandedApiStep === 'invoices' ? 'bg-blue-50' : 'bg-white'}`}
                    ref={activeApiStep === 'invoices' ? lastStepApiRef : null}
                    >
                    <button
                      type="button"
                      onClick={() => {
                        const newExpanded = expandedApiStep === 'invoices' ? null : 'invoices';
                        setExpandedApiStep(newExpanded);
                        if (newExpanded === 'invoices') {
                          setActiveApiStep('invoices');
                        }
                      }}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="absolute -left-10 w-8 h-8 bg-blue-500 font-semibold border-2 text-white flex-shrink-0 flex items-center justify-center rounded-full text-sm z-10">
                          2
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">Invoice</div>
                          <div className="text-sm text-gray-500">API endpoint for sending Invoice data</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${healthCheckCompletedStates['invoices-POST']
                          ? 'bg-green-50 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                          }`}>
                          {healthCheckCompletedStates['invoices-POST'] ? 'Complete' : 'Pending'}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedApiStep === 'invoices' ? 'rotate-180' : ''
                          }`} />
                      </div>
                    </button>

                    {expandedApiStep === 'invoices' && (
                      <div className="border-t border-gray-200 p-6 bg-white">
                        <div className="bg-gray-50 rounded-lg p-4 relative overflow-x-auto">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex space-x-1">
                              {([
                                { method: 'POST', label: 'CREATE' },
                                { method: 'PUT', label: 'UPDATE' },
                                { method: 'DELETE', label: 'DELETE' }
                              ] as const).map(({ method, label }) => (
                                <button
                                  key={method}
                                  onClick={() => setActiveMethod(method)}
                                  className={`px-3 py-1 text-sm font-medium rounded transition-colors ${activeMethod === method
                                    ? 'bg-primaryColor text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <button
                              onClick={handleCopyCurl}
                              className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 transition-colors"
                              title="Copy to clipboard"
                            >
                              {copiedCurl ? (
                                <>
                                  <CheckCircle className="w-3 h-3 text-green-600" />
                                  <span className="text-green-600">Copied!</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-3 h-3" />
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                          </div>
                          <SyntaxHighlighter
                            language="javascript"
                            style={githubGist}
                            customStyle={{
                              fontSize: '0.875rem',
                              background: '#f9fafb',
                              borderRadius: '.5rem',
                              padding: '1rem',
                              maxWidth: '100%',
                              overflowX: 'auto'
                            }}
                            codeTagProps={{ style: { fontFamily: 'Menlo, monospace' } }}
                          >
                            {curlCommands.invoices[activeMethod]}
                          </SyntaxHighlighter>
                        </div>

                        {/* Info Message */}
                        <div className="bg-amber-50 border rounded-lg p-3 mt-4">
                          <p className="text-amber-900 text-sm">
                            Use the above create cURL commands to send events to complete the Step.
                          </p>
                        </div>

                        {/* Events Status for Invoices */}
                        <div className="mt-4 p-4 border rounded-lg bg-blue-50">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="flex items-center justify-center w-6 h-6">
                              {healthCheckCompletedStates['invoices-POST'] ? (
                                <CheckCircle className="w-6 h-6 text-green-600 font-bold" />
                              ) : (
                                <Loader2 className="w-6 h-6 text-blue-500 animate-spin font-bold" strokeWidth={4} />
                              )}
                            </span>
                            <span className={` font-medium ${healthCheckCompletedStates['invoices-POST'] ? 'text-green-700' : 'text-blue-700'
                              }`}>
                              {healthCheckCompletedStates['invoices-POST']
                                ? 'Invoice events detected successfully'
                                : 'Waiting for events...'}
                            </span>
                          </div>

                          {/* Recent Events Table for Invoices */}
                          {expandedApiStep === 'invoices' && eventsByEntity.invoices.length > 0 && (
                            <div className="mt-4 transition-all duration-500 ease-out opacity-100 transform translate-y-0">
                              <div className="bg-white rounded-lg border border-blue-200 p-4 ms-8">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Activity className="w-5 h-5 text-blue-600" />
                                  <h4 className="font-medium text-blue-800">Recent Invoice Events</h4>
                                </div>
                                <div className="overflow-x-auto">
                                  <table className="w-full">
                                    <thead>
                                      <tr className="bg-gray-50 text-left">
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Event Type</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Operation</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Status</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Timestamp</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Data</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {eventsByEntity.invoices.map((event, index) => (
                                        <tr
                                          key={`${event.id}-${index}`}
                                          style={{
                                            opacity: 0,
                                            transform: 'translateY(20px)',
                                            animation: `fade-in-up 0.5s ease-out ${index * 0.1}s forwards`,
                                          }}
                                        >
                                          <td className="px-4 py-3">
                                            <div className="flex items-center space-x-2">
                                              <Activity className="w-4 h-4 text-blue-600" />
                                              <span className="font-medium text-gray-900">{event.record_type}</span>
                                            </div>
                                          </td>
                                          <td className="px-4 py-3">
                                            <span className="font-medium text-gray-900">
                                              {event.operation_type || '-'}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${event.processing_status === 'COMPLETED'
                                              ? 'bg-green-50 text-green-800'
                                              : event.processing_status === 'FAILED'
                                                ? 'bg-red-500 text-red-800'
                                                : 'bg-yellow-100 text-yellow-800'
                                              }`}>
                                              {event.processing_status || 'PENDING'}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3 text-gray-600">
                                            {formatTime(event.timestamp)}
                                          </td>
                                          <td className="px-4 py-3">
                                            <button
                                              onClick={() => {
                                                setSelectedEventData(event.originalData);
                                                setShowEventDataModal(true);
                                              }}
                                              className="text-blue-600 hover:text-blue-800 font-mono text-sm px-2 py-1 hover:bg-blue-50 transition-colors"
                                              title="View original JSON data"
                                            >
                                              ...
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Subscriptions Step */}
                  <div 
                    className={`border border-gray-200 rounded-lg ${expandedApiStep === 'subscriptions' ? 'bg-blue-50' : 'bg-white'}`}
                    ref={activeApiStep === 'subscriptions' ? lastStepApiRef : null}
                    >
                    <button
                      type="button"
                      onClick={() => {
                        const newExpanded = expandedApiStep === 'subscriptions' ? null : 'subscriptions';
                        setExpandedApiStep(newExpanded);
                        if (newExpanded === 'subscriptions') {
                          setActiveApiStep('subscriptions');
                        }
                      }}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="absolute -left-10 w-8 h-8 bg-blue-500 font-semibold border-2 text-white flex-shrink-0 flex items-center justify-center rounded-full text-sm z-10">
                          3
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">Subscription</div>
                          <div className="text-sm text-gray-500">API endpoint for sending Subscription data</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${healthCheckCompletedStates['subscriptions-POST']
                          ? 'bg-green-50 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                          }`}>
                          {healthCheckCompletedStates['subscriptions-POST'] ? 'Complete' : 'Pending'}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedApiStep === 'subscriptions' ? 'rotate-180' : ''
                          }`} />
                      </div>
                    </button>

                    {expandedApiStep === 'subscriptions' && (
                      <div className="border-t border-gray-200 p-6 bg-white">
                        <div className="bg-gray-50 rounded-lg p-4 relative overflow-x-auto">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex space-x-1">
                              {([
                                { method: 'POST', label: 'CREATE' },
                                { method: 'PUT', label: 'UPDATE' },
                                { method: 'DELETE', label: 'DELETE' }
                              ] as const).map(({ method, label }) => (
                                <button
                                  key={method}
                                  onClick={() => setActiveMethod(method)}
                                  className={`px-3 py-1 text-sm font-medium rounded transition-colors ${activeMethod === method
                                    ? 'bg-primaryColor text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <button
                              onClick={handleCopyCurl}
                              className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 transition-colors"
                              title="Copy to clipboard"
                            >
                              {copiedCurl ? (
                                <>
                                  <CheckCircle className="w-3 h-3 text-green-600" />
                                  <span className="text-green-600">Copied!</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-3 h-3" />
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                          </div>
                          <SyntaxHighlighter
                            language="javascript"
                            style={githubGist}
                            customStyle={{
                              fontSize: '0.875rem',
                              background: '#f9fafb',
                              borderRadius: '.5rem',
                              padding: '1rem',
                              maxWidth: '100%',
                              overflowX: 'auto'
                            }}
                            codeTagProps={{ style: { fontFamily: 'Menlo, monospace' } }}
                          >
                            {curlCommands.subscriptions[activeMethod]}
                          </SyntaxHighlighter>
                        </div>

                        {/* Info Message */}
                        <div className="bg-amber-50 border rounded-lg p-3 mt-4">
                          <p className="text-amber-900 text-sm">
                            Use the above create cURL commands to send events to complete the Step.
                          </p>
                        </div>

                        {/* Events Status for Subscriptions */}
                        <div className="mt-4 p-4 border rounded-lg bg-blue-50">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="flex items-center justify-center w-6 h-6">
                              {healthCheckCompletedStates['subscriptions-POST'] ? (
                                <CheckCircle className="w-6 h-6 text-green-600 font-bold" />
                              ) : (
                                <Loader2 className="w-6 h-6 text-blue-500 animate-spin font-bold" strokeWidth={4} />
                              )}
                            </span>
                            <span className={` font-medium ${healthCheckCompletedStates['subscriptions-POST'] ? 'text-green-700' : 'text-blue-700'
                              }`}>
                              {healthCheckCompletedStates['subscriptions-POST']
                                ? 'Subscription events detected successfully'
                                : 'Waiting for events...'}
                            </span>
                          </div>

                          {/* Recent Events Table for Subscriptions */}
                          {expandedApiStep === 'subscriptions' && eventsByEntity.subscriptions.length > 0 && (
                            <div className="mt-4 transition-all duration-500 ease-out opacity-100 transform translate-y-0">
                              <div className="bg-white rounded-lg border border-blue-200 p-4 ms-8">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Activity className="w-5 h-5 text-blue-600" />
                                  <h4 className="font-medium text-blue-800">Recent Subscription Events</h4>
                                </div>
                                <div className="overflow-x-auto">
                                  <table className="w-full">
                                    <thead>
                                      <tr className="bg-gray-50 text-left">
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Event Type</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Operation</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Status</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Timestamp</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Data</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {eventsByEntity.subscriptions.map((event, index) => (
                                        <tr
                                          key={`${event.id}-${index}`}
                                          style={{
                                            opacity: 0,
                                            transform: 'translateY(20px)',
                                            animation: `fade-in-up 0.5s ease-out ${index * 0.1}s forwards`,
                                          }}
                                        >
                                          <td className="px-4 py-3">
                                            <div className="flex items-center space-x-2">
                                              <Activity className="w-4 h-4 text-blue-600" />
                                              <span className="font-medium text-gray-900">{event.record_type}</span>
                                            </div>
                                          </td>
                                          <td className="px-4 py-3">
                                            <span className="font-medium text-gray-900">
                                              {event.operation_type || '-'}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${event.processing_status === 'COMPLETED'
                                              ? 'bg-green-50 text-green-800'
                                              : event.processing_status === 'FAILED'
                                                ? 'bg-red-500 text-red-800'
                                                : 'bg-yellow-100 text-yellow-800'
                                              }`}>
                                              {event.processing_status || 'PENDING'}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3 text-gray-600">
                                            {formatTime(event.timestamp)}
                                          </td>
                                          <td className="px-4 py-3">
                                            <button
                                              onClick={() => {
                                                setSelectedEventData(event.originalData);
                                                setShowEventDataModal(true);
                                              }}
                                              className="text-blue-600 hover:text-blue-800 font-mono text-sm px-2 py-1 hover:bg-blue-50 transition-colors"
                                              title="View original JSON data"
                                            >
                                              ...
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Subscription Items Step */}
                  <div 
                    className={`border border-gray-200 rounded-lg ${expandedApiStep === 'subscription-items' ? 'bg-blue-50' : 'bg-white'}`}
                    ref={activeApiStep === 'subscription-items' ? lastStepApiRef : null}
                    >
                    <button
                      type="button"
                      onClick={() => {
                        const newExpanded = expandedApiStep === 'subscription-items' ? null : 'subscription-items';
                        setExpandedApiStep(newExpanded);
                        if (newExpanded === 'subscription-items') {
                          setActiveApiStep('subscription-items');
                        }
                      }}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="absolute -left-10 w-8 h-8 bg-blue-500 font-semibold border-2 text-white flex-shrink-0 flex items-center justify-center rounded-full text-sm z-10">
                          4
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">Subscription Items</div>
                          <div className="text-sm text-gray-500">API endpoint for sending Subscription Items data</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${healthCheckCompletedStates['subscription-items-POST']
                          ? 'bg-green-50 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                          }`}>
                          {healthCheckCompletedStates['subscription-items-POST'] ? 'Complete' : 'Pending'}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedApiStep === 'subscription-items' ? 'rotate-180' : ''
                          }`} />
                      </div>
                    </button>

                    {expandedApiStep === 'subscription-items' && (
                      <div className="border-t border-gray-200 p-6 bg-white">
                        <div className="bg-gray-50 rounded-lg p-4 relative overflow-x-auto">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex space-x-1">
                              {([
                                { method: 'POST', label: 'CREATE' },
                                { method: 'PUT', label: 'UPDATE' },
                                { method: 'DELETE', label: 'DELETE' }
                              ] as const).map(({ method, label }) => (
                                <button
                                  key={method}
                                  onClick={() => setActiveMethod(method)}
                                  className={`px-3 py-1 text-sm font-medium rounded transition-colors ${activeMethod === method
                                    ? 'bg-primaryColor text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <button
                              onClick={handleCopyCurl}
                              className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 transition-colors"
                              title="Copy to clipboard"
                            >
                              {copiedCurl ? (
                                <>
                                  <CheckCircle className="w-3 h-3 text-green-600" />
                                  <span className="text-green-600">Copied!</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-3 h-3" />
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                          </div>
                          <SyntaxHighlighter
                            language="javascript"
                            style={githubGist}
                            customStyle={{
                              fontSize: '0.875rem',
                              background: '#f9fafb',
                              borderRadius: '.5rem',
                              padding: '1rem',
                              maxWidth: '100%',
                              overflowX: 'auto'
                            }}
                            codeTagProps={{ style: { fontFamily: 'Menlo, monospace' } }}
                          >
                            {curlCommands['subscription-items'][activeMethod]}
                          </SyntaxHighlighter>
                        </div>

                        {/* Info Message */}
                        <div className="bg-amber-50 border rounded-lg p-3 mt-4">
                          <p className="text-amber-900 text-sm">
                            Use the above create cURL commands to send events to complete the Step.
                          </p>
                        </div>

                        {/* Events Status for Subscription Items */}
                        <div className="mt-4 p-4 border rounded-lg bg-blue-50">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="flex items-center justify-center w-6 h-6">
                              {healthCheckCompletedStates['subscription-items-POST'] ? (
                                <CheckCircle className="w-6 h-6 text-green-600 font-bold" />
                              ) : (
                                <Loader2 className="w-6 h-6 text-blue-500 animate-spin font-bold" strokeWidth={4} />
                              )}
                            </span>
                            <span className={` font-medium ${healthCheckCompletedStates['subscription-items-POST'] ? 'text-green-700' : 'text-blue-700'
                              }`}>
                              {healthCheckCompletedStates['subscription-items-POST']
                                ? 'Subscription Item events detected successfully'
                                : 'Waiting for events...'}
                            </span>
                          </div>

                          {/* Recent Events Table for Subscription Items */}
                          {expandedApiStep === 'subscription-items' && eventsByEntity['subscription-items'].length > 0 && (
                            <div className="mt-4 transition-all duration-500 ease-out opacity-100 transform translate-y-0">
                              <div className="bg-white rounded-lg border border-blue-200 p-4 ms-8">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Activity className="w-5 h-5 text-blue-600" />
                                  <h4 className="font-medium text-blue-800">Recent Subscription Item Events</h4>
                                </div>
                                <div className="overflow-x-auto">
                                  <table className="w-full">
                                    <thead>
                                      <tr className="bg-gray-50 text-left">
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Event Type</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Operation</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Status</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Timestamp</th>
                                        <th className="px-4 py-2 text-sm font-medium text-gray-600">Data</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {eventsByEntity['subscription-items'].map((event, index) => (
                                        <tr
                                          key={`${event.id}-${index}`}
                                          style={{
                                            opacity: 0,
                                            transform: 'translateY(20px)',
                                            animation: `fade-in-up 0.5s ease-out ${index * 0.1}s forwards`,
                                          }}
                                        >
                                          <td className="px-4 py-3">
                                            <div className="flex items-center space-x-2">
                                              <Activity className="w-4 h-4 text-blue-600" />
                                              <span className="font-medium text-gray-900">{event.record_type}</span>
                                            </div>
                                          </td>
                                          <td className="px-4 py-3">
                                            <span className="font-medium text-gray-900">
                                              {event.operation_type || '-'}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${event.processing_status === 'COMPLETED'
                                              ? 'bg-green-50 text-green-800'
                                              : event.processing_status === 'FAILED'
                                                ? 'bg-red-500 text-red-800'
                                                : 'bg-yellow-100 text-yellow-800'
                                              }`}>
                                              {event.processing_status || 'PENDING'}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3 text-gray-600">
                                            {formatTime(event.timestamp)}
                                          </td>
                                          <td className="px-4 py-3">
                                            <button
                                              onClick={() => {
                                                setSelectedEventData(event.originalData);
                                                setShowEventDataModal(true);
                                              }}
                                              className="text-blue-600 hover:text-blue-800 font-mono text-sm px-2 py-1 hover:bg-blue-50 transition-colors"
                                              title="View original JSON data"
                                            >
                                              ...
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <style>{`
                  @keyframes fade-in-up {
                    from {
                      opacity: 0;
                      transform: translateY(20px);
                    }
                    to {
                      opacity: 1;
                      transform: translateY(0);
                    }
                  }
                `}
                </style>
              </div>

            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStripeConfiguration = () => {
    return (
      <StripeIntegration
        sourceConfigurations={sourceConfigurations}
        updateSourceConfiguration={updateSourceConfiguration}
        loadedStripeData={loadedStripeData}
        selectedSources={selectedSources[0]}
        config={configStripe}
        setConfig={setConfigStripe}
        onStepComplete={onStepComplete}
        emailManager={emailManager}
        onAutoSave={onAutoSave}
        cleanOAuthParams={cleanOAuthParams}
        openSetupGuideRef={openSetupGuideRef}
        onOpenIntegrationsList={onOpenIntegrationsList}
        onTrackCompletion={onTrackCompletion}
      />
    );
  };

  const renderChargebeeConfiguration = () => {
    return (
      <ChargebeeIntegration
        sourceConfigurations={sourceConfigurations}
        updateSourceConfiguration={updateSourceConfiguration}
        loadedChargebeeData={sourceConfigurations?.chargebee}
        selectedSources={selectedSources[0]}
        config={configChargebee}
        setConfig={setConfigChargebee}
        onStepComplete={onStepComplete}
        emailManager={emailManager}
        onAutoSave={onAutoSave}
        cleanOAuthParams={cleanOAuthParams}
        openSetupGuideRef={openSetupGuideRef}
        onOpenIntegrationsList={onOpenIntegrationsList}
        onTrackCompletion={onTrackCompletion}
      />
    );
  };

  const renderSalesforceConfiguration = () => {
    return (
      <div className="space-y-6">
        {/* Information Card */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Building2 className="w-5 h-5 text-orange-600 mt-1" />
            <div className="flex-1">
              <h3 className="font-medium text-orange-900 mb-2">Salesforce Integration</h3>
              <p className="text-orange-700 text-sm mb-3">
                Connect your Salesforce CRM to import customer and sales data.
                This integration will provide access to:
              </p>
              <ul className="text-sm text-orange-700 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-600" />
                  Account and contact information
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-600" />
                  Opportunity and lead data
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-600" />
                  Sales pipeline metrics
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-600" />
                  Custom objects and fields
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Configuration Form */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Connection Settings</h3>

          <div className="space-y-4">
            {/* Instance URL */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Instance URL</label>
              <input
                type="text"
                placeholder="https://your-org.my.salesforce.com"
                value={salesforceInstanceUrl}
                onChange={(e) => setSalesforceInstanceUrl(e.target.value)}
                onBlur={handleSaveSalesforceConfig}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500">
                Your Salesforce instance URL (found in your Salesforce setup)
              </p>
            </div>

            {/* Username */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Username</label>
              <input
                type="email"
                placeholder="your-username@company.com"
                value={salesforceUsername}
                onChange={(e) => setSalesforceUsername(e.target.value)}
                onBlur={handleSaveSalesforceConfig}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <div className="relative">
                <input
                  type={showSalesforcePassword ? 'text' : 'password'}
                  placeholder="Your Salesforce password"
                  value={salesforcePassword}
                  onChange={(e) => setSalesforcePassword(e.target.value)}
                  onBlur={handleSaveSalesforceConfig}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  onClick={() => setShowSalesforcePassword(!showSalesforcePassword)}
                >
                  {showSalesforcePassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                </button>
              </div>
            </div>

            {/* Security Token */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Security Token</label>
              <div className="relative">
                <input
                  type={showSalesforceToken ? 'text' : 'password'}
                  placeholder="Your security token"
                  value={salesforceSecurityToken}
                  onChange={(e) => setSalesforceSecurityToken(e.target.value)}
                  onBlur={handleSaveSalesforceConfig}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  onClick={() => setShowSalesforceToken(!showSalesforceToken)}
                >
                  {showSalesforceToken ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Get your security token from{' '}
                <a
                  href="https://help.salesforce.com/s/articleView?id=sf.user_security_token.htm"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  Salesforce Settings
                  <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* API Access Requirements */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">API Access Requirements</h3>
          <div className="space-y-3 text-sm text-gray-600">
            <p>To use this integration, ensure your Salesforce org has:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                'API access enabled',
                'User has API permissions',
                'Required object permissions',
                'IP address allowlisted (if restricted)',
              ].map((requirement) => (
                <div key={requirement} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                  <span>{requirement}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Available Data Objects */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Available Data Objects</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {[
              'Accounts',
              'Contacts',
              'Opportunities',
              'Leads',
              'Cases',
              'Products',
              'Quotes',
              'Contracts',
              'Campaigns',
            ].map((object) => (
              <span
                key={object}
                className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded text-center"
              >
                {object}
              </span>
            ))}
          </div>
        </div>

        {/* Test Connection */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <button
            disabled={!salesforceInstanceUrl || !salesforceUsername || !salesforcePassword || !salesforceSecurityToken}
            onClick={() => console.log('Test Salesforce connection')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Test Connection
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Verify your Salesforce credentials and API access
          </p>
        </div>
      </div>
    );
  };

  const renderQuickBooksConfiguration = () => (
    <QuickBooksIntegration
      sourceConfigurations={sourceConfigurations}
      updateSourceConfiguration={updateSourceConfiguration}
      onStepComplete={onStepComplete}
      emailManager={emailManager}
      onAutoSave={onAutoSave}
      cleanOAuthParams={cleanOAuthParams}
      openSetupGuideRef={openSetupGuideRef}
      onTrackCompletion={onTrackCompletion}
    />
  );

  const renderConfiguration = () => {
    switch (activeSourceTab) {
      case 'csv':
        return renderCSVConfiguration();
      case 'api':
        return renderAPIConfiguration();
      case 'stripe':
        return renderStripeConfiguration();
      case 'chargebee':
        return renderChargebeeConfiguration();
      case 'salesforce':
        return renderSalesforceConfiguration();
      case 'quickbooks':
        return renderQuickBooksConfiguration();
      default:
        return <div>Unknown configuration type</div>;
    }
  };

  const getSourceStatus = (sourceType: string) => {
    const config = sourceConfigurations[sourceType];
    if (!config) return 'not_configured';
    return config.status || 'not_configured';
  };

  const renderStep2SubStep = (subStepIdx: number) => {
    const subStep = step2Configs[subStepIdx];
    const uploadedFile = step2UploadedFiles[subStepIdx];
    const csvHeaders = step2CsvHeaders[subStepIdx];
    const fieldMapping = step2FieldMappings[subStepIdx];
    const importResult = step2ImportResults[subStepIdx];
    const isProcessing = step2IsProcessing[subStepIdx];

    const requiredFields = subStep.fields.required;
    const optionalFields = subStep.fields.optional;
    const status = getStepStatus(subStepIdx);

    const allRequiredFieldsMapped = requiredFields.every((field: any) => fieldMapping[field.fieldName]);

    return (
      <div className="space-y-6">
        <div className="rounded-lg p-3 bg-amber-50 text-amber-900">
          <div className="flex items-start space-x-3">
            <Download className="w-5 h-4 mt-1 text-amber-900" />
            <div className="flex">
              <h3 className="font-medium">Need a sample file?</h3>
              <button
                onClick={() => downloadStep2Sample(subStep.sampleFileName, selectedDataType)}
                className="ml-1 font-medium text-sm underline"
                style={{ color: '#2563eb' }}
                onMouseOver={e => (e.currentTarget.style.color = '#1d4ed8')}
                onMouseOut={e => (e.currentTarget.style.color = '#2563eb')}
              >
                Download Sample Data
              </button>
            </div>
          </div>
        </div>

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={e => handleStep2FileUpload(subStepIdx, e)}
            className="hidden"
          />
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Drop your CSV file here or click to browse</p>
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{ background: '#2563eb', color: '#ffffff' }}
            className="px-6 py-2 rounded-lg transition-colors"
            onMouseOver={e => (e.currentTarget.style.background = '#1d4ed8')}
            onMouseOut={e => (e.currentTarget.style.background = '#2563eb')}
          >
            Choose File
          </button>
        </div>

        {uploadedFile && (
          <div className={`border rounded-lg p-4`}>
            <div className="flex items-center space-x-2">
              {status === 'error' ? (
                <AlertTriangle className="w-5 h-5 text-red-600" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
              <span className={`font-medium ms-6 ${status === 'error' ? 'text-red-600' : 'text-green-800'
                }`}>
                {status === 'error' ? 'File uploaded with errors' : 'File uploaded successfully'}
              </span>
            </div>
            <p className={`text-sm mt-1 ms-[26px] border border-gray-50 ${status === 'error' ? 'text-red-600' : 'text-green-700'
              }`}>
              {uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(1)} KB)
            </p>
          </div>
        )}

        {uploadedFile && csvHeaders.length > 0 && (
          <div ref={el => { if (mapFieldsRefs.current) mapFieldsRefs.current[subStepIdx] = el as HTMLDivElement | null; }} className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Map Your Fields</h3>
              <p className="text-gray-600">Map your CSV columns to the required and optional fields</p>
            </div>

            {requiredFields.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 text-sm flex items-center">
                  <span className="mr-1">*</span>
                  Required Fields
                </h4>
                {requiredFields.map((field: any) => (
                  <div key={field.fieldName} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">
                          {field.fieldName}{" "}
                          <span className="text-lg mr-1">*</span>
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">{field.description}</p>
                      </div>
                      <div className="ml-4">
                        <select
                          value={fieldMapping[field.fieldName] || ''}
                          onChange={e =>
                            handleStep2FieldMappingChange(subStepIdx, field.fieldName, e.target.value)
                          }
                          className="block w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select a field...</option>
                          {csvHeaders.map((header) => (
                            <option key={header} value={header}>{header}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}


            {optionalFields.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 text-sm flex items-center">
                  <span className="inline-block w-2 h-2 rounded-full mr-2"></span>
                  Optional Fields
                </h4>
                {optionalFields.map((field: any) => (
                  <div key={field.fieldName} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{field.fieldName}</h3>
                        <p className="text-sm text-gray-600 mt-1">{field.description}</p>
                      </div>
                      <div className="ml-4">
                        <select
                          value={fieldMapping[field.fieldName] || ''}
                          onChange={e => handleStep2FieldMappingChange(subStepIdx, field.fieldName, e.target.value)}
                          className="block w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select a field (optional)...</option>
                          {csvHeaders.map((header) => (
                            <option key={header} value={header}>{header}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-amber-50 border text-amber-900 rounded-lg p-4">
              <h4 className="font-medium mb-2">Mapping Summary</h4>
              <div className="text-sm space-y-1">
                <div>Required fields mapped: {requiredFields.filter((f: any) => fieldMapping[f.fieldName]).length} / {requiredFields.length}</div>
                <div>Optional fields mapped: {optionalFields.filter((f: any) => fieldMapping[f.fieldName]).length} / {optionalFields.length}</div>
              </div>
            </div>

            <div className="flex gap-4 justify-end">
              {isProcessing && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-white text-xs">ℹ</span>
                    </div>
                    <div className="flex-1">
                      {/* Progress Bar */}
                      <div className="mb-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-blue-700 font-medium">Processing...</span>
                          <span className="text-xs text-blue-700">{Math.round(progressPercentage)}%</span>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-200 ease-out"
                            style={{ width: `${progressPercentage}%` }}
                          ></div>
                        </div>
                      </div>

                      <span className="text-blue-800 text-sm font-medium">
                        You can proceed to upload other data while this processes.
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <button
                ref={el => { if (nextButtonRefs.current) nextButtonRefs.current[subStepIdx] = el as HTMLButtonElement | null; }}
                onClick={() => handleImportButtonClick(subStepIdx, status === 'error')}
                disabled={isProcessing || !allRequiredFieldsMapped}
                className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-colors ${isProcessing || !allRequiredFieldsMapped ? 'cursor-not-allowed' : ''
                  }`}
                style={{
                  background: isProcessing || !allRequiredFieldsMapped ? '#d1d5db' :
                    status === 'error' ? '#dc2626' : '#2563eb',
                  color: isProcessing || !allRequiredFieldsMapped ? '#6b7280' : '#ffffff',
                }}
                onMouseOver={e => {
                  if (!(isProcessing || !allRequiredFieldsMapped)) {
                    e.currentTarget.style.background = status === 'error' ? '#b91c1c' : '#1d4ed8';
                  }
                }}
                onMouseOut={e => {
                  if (!(isProcessing || !allRequiredFieldsMapped)) {
                    e.currentTarget.style.background = status === 'error' ? '#dc2626' : '#2563eb';
                  }
                }}
              >
                {getImportButtonText(status, isProcessing)}
                {!isProcessing && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}

        {renderImportResults(importResult, subStepIdx)}
      </div>
    );
  };

  return (
    <div className="space-y-6 ms-10">
      {/* Field Mapping Confirmation Modal */}
      <FieldMappingConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirmImport}
        onEdit={handleEditMapping}
        subStep={pendingImportSubStepIdx !== null ? step2Configs[pendingImportSubStepIdx] : null}
        fieldMapping={pendingImportSubStepIdx !== null ? step2FieldMappings[pendingImportSubStepIdx] : {}}
        isRetry={isRetryAction}
      />

      {/* Tab Content */}
      {renderConfiguration()}

      {/* Create API Key Modal */}
      {showCreateApiKeyModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-20">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create API Key</h3>
              <button
                onClick={handleCreateApiKeyCancel}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={createApiKeyForm.name}
                  onChange={(e) => setCreateApiKeyForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter API key name"
                />
              </div>

              {/* Expiration Date Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={createApiKeyForm.expirationDate}
                    onChange={(e) => setCreateApiKeyForm(prev => ({ ...prev, expirationDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <Calendar className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Scope Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scope of the API Key <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={createApiKeyForm.scope}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-gray-100 cursor-not-allowed"
                  >
                    <option value="revenue_apis">Revenue APIs</option>
                  </select>
                  <ChevronDownIcon className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
                <p className="text-xs text-gray-500 mt-1">Scope is automatically set to Revenue APIs for this setup</p>
              </div>

              {/* Environment Field - Last */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Environment <span className="text-red-500">*</span>
                </label>
                <div className="relative" data-dropdown="environment">
                  <button
                    type="button"
                    onClick={() => setShowEnvironmentDropdown(!showEnvironmentDropdown)}
                    disabled={environmentsLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 text-left flex items-center justify-between"
                  >
                    <span className={createApiKeyForm.environmentName ? 'text-gray-900' : 'text-gray-500'}>
                      {createApiKeyForm.environmentName || 'Select Environment'}
                    </span>
                    <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showEnvironmentDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Custom Dropdown */}
                  {showEnvironmentDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                      <div className="py-1">
                        {environmentsLoading ? (
                          <div className="px-3 py-2 text-sm text-gray-500">Loading environments...</div>
                        ) : (
                          <>
                            {environmentsData?.getEnvironmentsForProduct?.environments?.map((env: any) => (
                              <button
                                key={env?.id}
                                type="button"
                                onClick={() => handleEnvironmentSelect(env)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 hover:text-blue-900 flex items-center justify-between"
                              >
                                <span>{env?.displayName}</span>
                                {createApiKeyForm.environmentId === env?.id && (
                                  <CheckCircle className="w-4 h-4 text-blue-600" />
                                )}
                              </button>
                            ))}

                            {/* New Environment Option */}
                            <div className="border-t border-gray-200">
                              <button
                                type="button"
                                onClick={handleNewEnvironment}
                                className="w-full px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 hover:text-blue-700 flex items-center space-x-2"
                              >
                                <span className="text-lg">+</span>
                                <span>New Environment</span>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCreateApiKeyCancel}
                className="px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateApiKeySubmit}
                disabled={!createApiKeyForm.name || !createApiKeyForm.expirationDate || !createApiKeyForm.environmentId || createApiKeyLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {createApiKeyLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>{createApiKeyLoading ? 'Creating...' : 'Create'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Environment Creation Modal */}
      {showNewEnvironmentForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-20">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create New Environment</h3>
              <button
                onClick={handleCancelNewEnvironment}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Environment Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Environment Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newEnvironmentForm.displayName}
                  onChange={(e) => setNewEnvironmentForm(prev => ({ ...prev, displayName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter environment name"
                />
              </div>

              {/* Environment Type Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Environment Type <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={newEnvironmentForm.environmentType}
                    onChange={(e) => setNewEnvironmentForm(prev => ({ ...prev, environmentType: e.target.value as EnvironmentType }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                  >
                    <option value={EnvironmentType.Production}>Production</option>
                    <option value={EnvironmentType.NonProduction}>NonProduction</option>
                  </select>
                  <ChevronDownIcon className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCancelNewEnvironment}
                className="px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNewEnvironment}
                disabled={!newEnvironmentForm.displayName || createEnvironmentLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {createEnvironmentLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>{createEnvironmentLoading ? 'Creating...' : 'Create Environment'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* JSON Data Modal */}
      {showEventDataModal && selectedEventData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-30">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Event JSON Data</h3>
              <button
                onClick={() => {
                  setShowEventDataModal(false);
                  setSelectedEventData(null);
                  setCopiedEventData(false);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 p-6 overflow-auto">
              <div className="bg-gray-50 rounded-lg p-4 overflow-auto">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-gray-700">Raw JSON Data</h4>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(selectedEventData);
                        setCopiedEventData(true);
                        setTimeout(() => setCopiedEventData(false), 2000);
                      } catch (err) {
                        console.error('Failed to copy:', err);
                      }
                    }}
                    className={`flex items-center space-x-1 px-2 py-1 text-xs transition-colors border rounded ${copiedEventData
                      ? 'text-green-600 border-green-300 bg-green-50'
                      : 'text-gray-600 hover:text-gray-900 border-gray-300 hover:bg-gray-100'
                      }`}
                    title="Copy to clipboard"
                  >
                    {copiedEventData ? (
                      <>
                        <CheckCircle className="w-3 h-3 text-green-600" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
                <SyntaxHighlighter
                  language="json"
                  style={githubGist}
                  customStyle={{
                    fontSize: '0.875rem',
                    background: '#f9fafb',
                    borderRadius: '.5rem',
                    padding: '1rem',
                    maxHeight: '400px',
                    overflow: 'auto'
                  }}
                  codeTagProps={{ style: { fontFamily: 'Menlo, monospace' } }}
                >
                  {selectedEventData}
                </SyntaxHighlighter>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowEventDataModal(false);
                  setSelectedEventData(null);
                  setCopiedEventData(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataSetup;
